import {
  AssistantMessage,
  AssistantTableState,
  ContextSummary,
  ContextText,
  PageContextV1
} from '@core/models/assistant/assistant.model';
import { capitalizeFirst, humanizeIdentifier } from '@core/utils/case-conversions';
import { getRelativePathFromUrl } from '@core/utils/dataspace-url.utils';

/**
 * Snapshot of what the user is looking at, sent with each assistant message. gbe-mcp validates it
 * strictly (unknown keys rejected, no nulls), so absent fields are omitted rather than set to null.
 */
export function buildPageContext(
  dataSpaceId: number,
  url: string,
  pinnedRouteUid: string | null,
  knownRouteUids: ReadonlySet<string>,
  table: AssistantTableState | null
): PageContextV1 {
  const segments = getRelativePathFromUrl(url).split('/').filter(Boolean);

  if (segments[0] === 'data') {
    if (!segments[1]) return { v: 1, page: 'dataspace.data', data_space_id: dataSpaceId };
    return withTable(
      {
        v: 1,
        page: `dataspace.data.${segments[1].toLowerCase()}`,
        data_space_id: dataSpaceId
      },
      segments[1],
      table
    );
  }
  if (segments[0] !== 'planning-spaces' || !segments[1])
    return { v: 1, page: 'dataspace', data_space_id: dataSpaceId };
  const planningSpaceId = decodeURIComponent(segments[1]);
  if (segments[2] !== 'plan' || !segments[3]) {
    return {
      v: 1,
      page: 'planning_space',
      data_space_id: dataSpaceId,
      planning_space_id: planningSpaceId
    };
  }

  const base = {
    v: 1 as const,
    data_space_id: dataSpaceId,
    planning_space_id: planningSpaceId,
    plan_id: decodeURIComponent(segments[3])
  };
  const rest = segments.slice(4);
  if (rest.length === 0) {
    // Marker clicks can leave non-route ids in the aggregator filter; only a known route uid is "this route".
    const pinned =
      pinnedRouteUid !== null && knownRouteUids.has(pinnedRouteUid) ? pinnedRouteUid : null;
    return pinned
      ? {
          ...base,
          page: 'plan.routes',
          selection: { entity_type: 'ROUTE', uids: [pinned] }
        }
      : { ...base, page: 'plan.routes' };
  }
  if (rest[0] === 'data' && rest[1])
    return withTable(
      { ...base, page: `plan.data.${rest[1].toLowerCase()}` },
      rest[1],
      table
    );
  return { ...base, page: `plan.${rest[0]}` };
}

/** gbe-mcp rejects the whole context, ids included, past these sizes. */
const MAX_SELECTION_UIDS = 100;
const MAX_SEARCH_CHARS = 1000;

function withTable(
  context: PageContextV1,
  urlEntity: string,
  table: AssistantTableState | null
): PageContextV1 {
  // A type other than the URL's is state left behind by another table.
  if (!table || table.entityType !== urlEntity.toUpperCase()) return context;
  const { entityType, uids, allMatching, query } = table;
  // A cut-down search would describe other rows, so a search too long to send drops the whole query.
  const view =
    query && (query.search ?? '').length <= MAX_SEARCH_CHARS
      ? {
          entity_type: entityType,
          ...(query.search ? { search: query.search } : {}),
          ...(query.filters !== undefined ? { filters: query.filters } : {})
        }
      : undefined;
  const listed = uids.slice(0, MAX_SELECTION_UIDS);
  let selection: PageContextV1['selection'];
  if (allMatching) {
    // The uids are only the fetched page; gbe-mcp reads a select-all through the view, so it needs one.
    if (view && query && listed.length > 0)
      selection = {
        entity_type: entityType,
        uids: listed,
        total: Math.max(query.total, listed.length),
        all_matching: true
      };
  } else if (listed.length > 0) {
    selection = {
      entity_type: entityType,
      uids: listed,
      ...(uids.length > listed.length ? { total: uids.length } : {})
    };
  }
  return {
    ...context,
    ...(selection ? { selection } : {}),
    ...(view ? { view } : {})
  };
}

export function describePageContext(
  context: PageContextV1,
  plan: { id: string; name: string } | null
): ContextSummary {
  const selected = selectionDetail(context);
  if (context.plan_id !== undefined) {
    const name: ContextText[] =
      plan?.id === context.plan_id
        ? [{ text: plan.name }]
        : [{ key: 'assistant.context.plan' }, { text: context.plan_id, mono: true }];
    const [, section, entity] = context.page.split('.');
    if (section === 'routes') {
      const route = context.selection?.uids[0];
      return {
        kind: 'plan',
        name,
        detail: route ? [{ key: 'assistant.context.route', params: { uid: route } }] : []
      };
    }
    if (section === 'data' && entity)
      return { kind: 'plan', name, detail: [{ text: entityLabel(entity) }, ...selected] };
    return { kind: 'plan', name, detail: [{ text: capitalizeFirst(section) }] };
  }
  if (context.planning_space_id !== undefined)
    return {
      kind: 'planning_space',
      name: [
        { key: 'assistant.context.planning_space' },
        { text: context.planning_space_id, mono: true }
      ],
      detail: []
    };
  const entity = context.page.split('.')[2];
  if (entity)
    return { kind: 'data', name: [{ text: entityLabel(entity) }], detail: selected };
  if (context.page === 'dataspace.data')
    return { kind: 'data', name: [{ key: 'assistant.context.data' }], detail: [] };
  return {
    kind: 'dataspace',
    name: [
      { key: 'assistant.context.data_space' },
      { text: String(context.data_space_id), mono: true }
    ],
    detail: []
  };
}

/** A message gets a caption when it is the first, or when its context differs from the previous message's. */
export function showsContext(messages: AssistantMessage[], index: number): boolean {
  const message = messages[index];
  if (message?.role !== 'user' || !message.context) return false;
  const previous = messages
    .slice(0, index)
    .reverse()
    .find((m) => m.role === 'user');
  return !previous || JSON.stringify(previous.context) !== JSON.stringify(message.context);
}

function selectionDetail(context: PageContextV1): ContextText[] {
  const selection = context.selection;
  if (!selection) return [];
  const count = selection.total ?? selection.uids.length;
  return [
    {
      key: selection.all_matching
        ? 'assistant.context.all_matching'
        : 'assistant.context.selected',
      params: { count }
    }
  ];
}

// The URL's entity type in sentence case; the breadcrumb's display names load asynchronously.
function entityLabel(entity: string): string {
  return humanizeIdentifier(entity);
}
